import React from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { Button } from '../../../components/widgets';
import Select from '../../../components/widgets/Select';
import { IconVariants } from '../../../components/foundations/Icon';
import FormField from '../FormField';
import DeleteIcon from '../DeleteIcon';
import Input from '../Input';
import { ATTRIBUTION_TAB_IDS, MACRO_INPUT_TYPES, MACRO_INPUT_TYPE_OPTIONS, TRANSLATE_PREFIX } from '../../consts';
import useConfirmDialog from '../../useConfirmDialog';
import useStableRowKeys from '../../useStableRowKeys';
import { useTabUpdater } from '../../formUpdaters';
import css from './AppLevelMacrosList.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const MACRO_PREFIX = `${TAB_PREFIX}.APP_LEVEL_MACRO`;
const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;

const macroHasContent = macro =>
    [macro.parameter_key_name, macro.field_name, macro.description, macro.allowed_values].some(
        v => typeof v === 'string' && v.trim() !== ''
    );

const AppLevelMacrosList = ({ readOnly = false, translate }) => {
    const { values } = useFormikContext();
    const updateTab = useTabUpdater(TAB_ID);
    const tabData = values.formData?.[TAB_ID] || {};
    const macros = tabData.app_level_macros || [];

    const { keysRef: rowKeysRef, makeKey: makeRowKey } = useStableRowKeys(macros.length, 'macro');

    const { confirm, dialog } = useConfirmDialog();

    // Mutators read prev macros via setValues(prev => ...) so a confirm-dialog resolution
    // racing another field update can't overwrite the other write with a stale snapshot.
    const updateMacrosFn = updater =>
        updateTab(prevTab => ({
            ...prevTab,
            app_level_macros: updater(prevTab.app_level_macros || []),
        }));

    const applyMacroUpdate = (index, field, value) => {
        updateMacrosFn(prevMacros => {
            const next = prevMacros.slice();
            next[index] = { ...next[index], [field]: value };
            if (field === 'input_type' && value !== MACRO_INPUT_TYPES.DROPDOWN) {
                next[index].allowed_values = undefined;
            }
            return next;
        });
    };

    const updateMacro = (index, field, value) => {
        const switchingAwayFromDropdown =
            field === 'input_type' && value !== MACRO_INPUT_TYPES.DROPDOWN && !!macros[index]?.allowed_values?.trim();
        if (switchingAwayFromDropdown) {
            confirm({
                titleKey: `${MACRO_PREFIX}.CONFIRM_DIALOG.CLEAR_ALLOWED_VALUES_TITLE`,
                textKey: `${MACRO_PREFIX}.CONFIRM_DIALOG.CLEAR_ALLOWED_VALUES_TEXT`,
                onConfirm: () => applyMacroUpdate(index, field, value),
            });
            return;
        }
        applyMacroUpdate(index, field, value);
    };

    const removeMacro = index => {
        rowKeysRef.current = rowKeysRef.current.filter((_, i) => i !== index);
        updateMacrosFn(prevMacros => prevMacros.filter((_, i) => i !== index));
    };

    const addMacro = () => {
        rowKeysRef.current = [...rowKeysRef.current, makeRowKey()];
        updateMacrosFn(prevMacros => [...prevMacros, { input_type: MACRO_INPUT_TYPES.TEXT }]);
    };

    const inputTypeOptions = MACRO_INPUT_TYPE_OPTIONS.map(t => ({
        value: t,
        label: translate(`${MACRO_PREFIX}.INPUT_TYPE_OPTIONS.${t.toUpperCase()}`),
    }));

    return (
        <div className={css.macrosList}>
            {macros.map((macro, i) => {
                const isDropdown = macro.input_type === MACRO_INPUT_TYPES.DROPDOWN;
                const selectedInputType = inputTypeOptions.find(o => o.value === macro.input_type) || null;
                return (
                    <div key={rowKeysRef.current[i]} className={css.macroCard}>
                        {!readOnly && (
                            <DeleteIcon
                                icon="trash"
                                className={css.removeButton}
                                confirmTitleKey={`${MACRO_PREFIX}.CONFIRM_DIALOG.REMOVE_MACRO_TITLE`}
                                confirmTextKey={`${MACRO_PREFIX}.CONFIRM_DIALOG.REMOVE_MACRO_TEXT`}
                                shouldConfirm={macroHasContent(macro)}
                                onConfirm={() => removeMacro(i)}
                            />
                        )}
                        <FormField
                            labelKey={`${MACRO_PREFIX}.PARAMETER_KEY_NAME_LABEL`}
                            subtitleKey={`${MACRO_PREFIX}.PARAMETER_KEY_NAME_SUBTITLE`}
                            required={!readOnly}
                            fullWidth
                            className={css.flatField}
                        >
                            <Input
                                value={macro.parameter_key_name || ''}
                                placeholder={translate(`${MACRO_PREFIX}.PARAMETER_KEY_NAME_PLACEHOLDER`)}
                                onChange={val => updateMacro(i, 'parameter_key_name', val)}
                                disabled={readOnly}
                            />
                        </FormField>
                        <div className={css.twoCol}>
                            <FormField
                                labelKey={`${MACRO_PREFIX}.FIELD_NAME_LABEL`}
                                subtitleKey={`${MACRO_PREFIX}.FIELD_NAME_SUBTITLE`}
                                fullWidth
                                className={css.flatField}
                            >
                                <Input
                                    value={macro.field_name || ''}
                                    placeholder={translate(`${MACRO_PREFIX}.FIELD_NAME_PLACEHOLDER`)}
                                    onChange={val => updateMacro(i, 'field_name', val)}
                                    disabled={readOnly}
                                />
                            </FormField>
                            <FormField
                                labelKey={`${MACRO_PREFIX}.DESCRIPTION_LABEL`}
                                subtitleKey={`${MACRO_PREFIX}.DESCRIPTION_SUBTITLE`}
                                fullWidth
                                className={css.flatField}
                            >
                                <Input
                                    value={macro.description || ''}
                                    placeholder={translate(`${MACRO_PREFIX}.DESCRIPTION_PLACEHOLDER`)}
                                    onChange={val => updateMacro(i, 'description', val)}
                                    disabled={readOnly}
                                />
                            </FormField>
                        </div>
                        <FormField
                            labelKey={`${MACRO_PREFIX}.INPUT_TYPE_LABEL`}
                            subtitleKey={`${MACRO_PREFIX}.INPUT_TYPE_SUBTITLE`}
                            fullWidth
                            className={css.flatField}
                        >
                            <Select
                                options={inputTypeOptions}
                                value={selectedInputType}
                                onChange={opt => updateMacro(i, 'input_type', opt ? opt.value : undefined)}
                                isSearchable={false}
                                isClearable={false}
                                isDisabled={readOnly}
                                className={css.inputTypeSelect}
                            />
                        </FormField>
                        {isDropdown && (
                            <FormField
                                labelKey={`${MACRO_PREFIX}.ALLOWED_VALUES_LABEL`}
                                required={!readOnly}
                                fullWidth
                                className={css.flatField}
                            >
                                <Input
                                    value={macro.allowed_values || ''}
                                    placeholder={translate(`${MACRO_PREFIX}.ALLOWED_VALUES_PLACEHOLDER`)}
                                    onChange={val => updateMacro(i, 'allowed_values', val)}
                                    disabled={readOnly}
                                />
                            </FormField>
                        )}
                    </div>
                );
            })}
            {!readOnly && (
                <Button
                    type="link"
                    Icon="plus"
                    iconVariant={IconVariants.ACTIVE}
                    onClick={addMacro}
                    className={css.addButton}
                >
                    <Translate id={`${MACRO_PREFIX}.ADD_ANOTHER`} />
                </Button>
            )}
            {dialog}
        </div>
    );
};

AppLevelMacrosList.propTypes = {
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(AppLevelMacrosList);
